import { IRecognizedBodySelectOption, RecognizedBodyStatus } from '@app-types/recognizedBody';
import { FieldErrorMessage, RadioGroupController, SelectController } from '@components';
import { useRecognizedBodies } from '@hooks';
import { Box, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCustomer } from '@store/customer/selectors';
import { SubmissionCreationFormValues, SubmissionRadioValue } from '../../types';
import { BorderedBlock } from '../styles';

const CounselingRecognizedBody = () => {
  const [isExternal, setIsExternal] = useState(false);
  const [isSelectionAllowed, setIsSelectionAllowed] = useState(false);

  const account = useSelector(selectCustomer);
  const { formatMessage } = useIntl();

  const { data } = useRecognizedBodies({
    status: RecognizedBodyStatus.Active,
  });

  const recognizedBodyOptions: IRecognizedBodySelectOption[] =
    data?.filter(
      (recognizedBody) => !!recognizedBody.responsibleForZIPs.split(',').find((zip) => zip.trim() === account?.zipCode),
    ).map(
      (recognizedBody) => ({ value: recognizedBody.id, label: recognizedBody.name }),
    ).sort((a, b) => a.label.localeCompare(b.label)) || [];

  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<SubmissionCreationFormValues>();

  const isUserAlreadyCounseling = watch('isUserAlreadyCounseling');

  const iframeExternalId = sessionStorage.getItem('iframeExternalId');

  useEffect(() => {
    if (!account || !iframeExternalId || !data)
      return;

    const hostSiteDomain = window.location.ancestorOrigins?.[0];

    // `data` holds active bodies only, so inactive bodies are inherently excluded from automatic
    // assignment. We deliberately select from these active bodies instead of the raw iframe id:
    // if the embedding body is inactive, no pre-fill happens and the form behaves like the regular website.
    const activeBodiesByHostSite = data.filter(
      (recognizedBody) => recognizedBody.approvedURLs.some((url) => hostSiteDomain.endsWith(url)),
    );

    if (activeBodiesByHostSite.length === 0)
      return;

    let selectedRecognizedBody = activeBodiesByHostSite[0];

    if (activeBodiesByHostSite.length > 1) {
      const recognizedBodiesByZIP = activeBodiesByHostSite.filter(
        (recognizedBody) => !!recognizedBody.responsibleForZIPs.split(',').find((zip) => zip.trim() === account.zipCode),
      );

      if (recognizedBodiesByZIP.length === 0)
        return;

      selectedRecognizedBody = recognizedBodiesByZIP[Math.floor(Math.random() * recognizedBodiesByZIP.length)];
    }

    setValue('isUserAlreadyCounseling', SubmissionRadioValue.Yes);
    setValue('recognizedBodyId', selectedRecognizedBody.id);
    setIsExternal(true);
  }, [iframeExternalId, data]);

  useEffect(() => {
    if (isExternal)
      return;

    // Hack to correctly update these two values to take into account the asynchronous update of data in the parent
    setTimeout(() => {
      switch (recognizedBodyOptions.length) {
        case 0:
          setValue('isUserAlreadyCounseling', SubmissionRadioValue.No);
          setIsSelectionAllowed(false);
          break;
        case 1:
          setValue('isUserAlreadyCounseling', SubmissionRadioValue.Yes);
          setValue('recognizedBodyId', recognizedBodyOptions[0].value);
          setIsSelectionAllowed(false);
          break;
        default:
          setIsSelectionAllowed(true);
          break;
      }
    }, 100);
  }, [recognizedBodyOptions.length, isExternal]);

  if (!isSelectionAllowed)
    return null;

  return (
    <BorderedBlock mt="32px" pb="27px" id="isUserAlreadyCounseling">
      <RadioGroupController
        row
        name="isUserAlreadyCounseling"
        control={control}
        label={formatMessage({ id: 'submissionCreation.ongoingService.isUserAlreadyCounseling' })}
        options={[
          { value: SubmissionRadioValue.Yes, label: formatMessage({ id: 'common.yes' }) },
          { value: SubmissionRadioValue.No, label: formatMessage({ id: 'common.no' }) },
        ]}
      />
      {isUserAlreadyCounseling === SubmissionRadioValue.Yes && (
        <Stack pl="2%" pr="2%" mt="32px" id="recognizedBodyId">
          <SelectController
            name="recognizedBodyId"
            control={control}
            label={formatMessage({ id: 'submissionCreation.ongoingService.chooseRecognizedBody' })}
            options={recognizedBodyOptions}
          />
        </Stack>
      )}
      {errors.recognizedBodyId?.message && (
        <Box mt={1}>
          <FieldErrorMessage message={errors.recognizedBodyId?.message} />
        </Box>
      )}
    </BorderedBlock>
  );
};

export default CounselingRecognizedBody;
